import AxiosInstance from "@/api/axios";
import {
  defaultPagination,
  PaginationInterface
} from "@/components/Base/Pagination";
import { GeneralFilterType, LeadsDataType } from "@/utils/types";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface leadsFilterType extends GeneralFilterType {
  search?: string | null;
  sort?: string | null;
  order?: string | null;
  currentPage?: number;
  perpage?: number;
  pageLimit: string;
  reengaged: string[];
}

let abortController: AbortController | null = null;

export const fetchLeads = createAsyncThunk(
  "fetchLeads/fetchData",
  async (
    {
      organization_id,
      agent,
      branch_id,
      region_id,
      date_range,
      search,
      currentPage,
      perpage,
      pageLimit,
      sort,
      order,
      reengaged
    }: leadsFilterType,
    thunkAPI
  ) => {
    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();
    console.log({
      organization_id,
      agent,
      branch_id,
      region_id,
      date_range,
      search,
      currentPage,
      perpage,
      pageLimit,
      sort,
      order,
      reengaged
    });
    try {
      const response = await AxiosInstance.get(`/leads`, {
        params: {
          page: currentPage,
          perpage,
          organization_id,
          branch_id,
          region_id,
          date_range,
          pageLimit,
          agent,
          search,
          sort,
          order,
          reengaged
        },
        signal: abortController.signal
      });
      if (response.data.success) {
        return response.data.data;
      } else {
        return null;
      }
    } catch (error: any) {
      if (!abortController.signal.aborted || error.code === "ERR_CANCELED") {
        return null;
      }
      return thunkAPI.rejectWithValue(error);
    } finally {
      abortController = null;
    }
  }
);

export const deleteLead = createAsyncThunk(
  "leads/deleteLead",
  async (
    {
      leadId,
      sender,
      branch,
      senders,
      region_id
    }: {
      leadId: string;
      sender?: string | null;
      branch?: string | null;
      senders?: string[];
      region_id?: string | null;
    },
    thunkAPI
  ) => {
    try {
      const params = new URLSearchParams();
      if (branch) params.append("branch", branch);
      if (region_id) params.append("region_id", region_id);
      if (senders && senders.length)
        params.append('senders', JSON.stringify(senders));
      if (!sender) {
        throw new Error('Sender not found');
      }
      const response = await AxiosInstance.delete(
        `/history/${sender}?branch=${branch}`
      );
      // const response = await AxiosInstance.delete(
      //   `/leads/${leadId}?${params.toString()}`
      // );

      return response.data.success
        ? leadId
        : thunkAPI.rejectWithValue("Failed to delete lead");
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || "An error occurred");
    }
  }
);

interface LeadsStateType {
  leads: {
    data: LeadsDataType[] | null | undefined;
    all_senders: string[][];
    pagination: PaginationInterface;
    timezone: string;
    showLeadFromColumn: boolean;
  };
  loading: boolean;
  error: string | null;
}

const initialState: LeadsStateType = {
  leads: {
    all_senders: [[]],
    data: null,
    pagination: defaultPagination,
    timezone: "",
    showLeadFromColumn: false
  },
  loading: false,
  error: null
};

const leadsReducer = createSlice({
  name: "leads",
  initialState,
  reducers: {
    updatePagination: (state, action) => {
      if (state.leads) {
        state.leads.pagination = {
          ...state.leads?.pagination,
          ...action.payload
        };
      }
    },
    resetPagination: (state, action) => {
      state.leads.pagination = defaultPagination;
    },
    updateLeadsOnAutomationSwitch: (state, action) => {
      const { sender, switchValue } = action.payload;
      if (state.leads.data) {
        const leadIndex = state.leads.data.findIndex(
          (lead) => lead.sender === sender
        );
        if (leadIndex !== -1) {
          state.leads.data[leadIndex].use_automation = switchValue;
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        state.loading = false;
        state.leads = action.payload;
      })
      .addCase(fetchLeads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteLead.fulfilled, (state, action) => {
        state.loading = false;

        if (state.leads.data) {
          state.leads.data = state.leads.data.filter(
            (lead) => lead._id !== action.payload
          );
        }
      })
      .addCase(deleteLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const {
  updatePagination,
  resetPagination,
  updateLeadsOnAutomationSwitch
} = leadsReducer.actions;

export default leadsReducer.reducer;
